import React, { useState, useEffect } from 'react';
import inspectionStorage from '../../services/inspectionStorage';
import syncManager from '../../services/syncManager';

export default function EssaiArretUrgenceModal({ isOpen, onClose, mission }) {
  const [essais, setEssais] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [currentEssai, setCurrentEssai] = useState({
    localisation: '',
    equipement: '',
    resultat: 'conforme',
    observations: ''
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadEssais();
    }
  }, [isOpen, mission]);

  const loadEssais = async () => {
    try {
      const data = await inspectionStorage.getStep(mission.id, 'etape_10_localisations');
      if (data?.essais_arret_urgence) {
        setEssais(data.essais_arret_urgence);
      }
    } catch (error) {
      console.error('Erreur chargement essais:', error);
    }
  };

  const saveEssais = async (essaisData) => {
    setIsSaving(true);
    try {
      const currentData = await inspectionStorage.getStep(mission.id, 'etape_10_localisations') || {};
      
      const updatedData = {
        ...currentData,
        essais_arret_urgence: essaisData,
        timestamp: new Date().toISOString()
      };

      await inspectionStorage.saveStep(mission.id, 'etape_10_localisations', updatedData);
      syncManager.syncStep(mission.id, 'etape_10_localisations', updatedData);
      
      console.log('✅ Essais d\'arrêt d\'urgence sauvegardés');
    } catch (error) {
      console.error('❌ Erreur sauvegarde essais:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddEssai = () => {
    if (!currentEssai.localisation || !currentEssai.equipement) {
      alert('Veuillez renseigner la localisation et l\'équipement');
      return;
    }

    const newEssais = [...essais];
    
    if (editingIndex !== null) {
      newEssais[editingIndex] = {
        ...currentEssai,
        date_essai: newEssais[editingIndex].date_essai || new Date().toISOString()
      };
      setEditingIndex(null);
    } else {
      newEssais.push({
        ...currentEssai,
        date_essai: new Date().toISOString()
      });
    }

    setEssais(newEssais);
    saveEssais(newEssais);
    setCurrentEssai({
      localisation: '',
      equipement: '',
      resultat: 'conforme',
      observations: ''
    });
    setShowAddForm(false);
  };

  const handleEditEssai = (essai, index) => {
    setCurrentEssai({
      localisation: essai.localisation,
      equipement: essai.equipement,
      resultat: essai.resultat,
      observations: essai.observations || ''
    });
    setEditingIndex(index);
    setShowAddForm(true);
  };

  const handleDeleteEssai = (index) => {
    if (window.confirm('Supprimer cet essai ?')) {
      const newEssais = essais.filter((_, i) => i !== index);
      setEssais(newEssais);
      saveEssais(newEssais);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">🛑 Essais d'Arrêt d'Urgence</h3>
          <button onClick={onClose} className="modal-close">✕</button>
        </div>

        <div className="modal-body">
          {isSaving && <p className="saving-indicator">💾 Sauvegarde...</p>}

          <button
            onClick={() => {
              setShowAddForm(!showAddForm);
              setEditingIndex(null);
              setCurrentEssai({
                localisation: '',
                equipement: '',
                resultat: 'conforme',
                observations: ''
              });
            }}
            className="add-button"
            style={{ marginBottom: '16px', width: '100%' }}
          >
            {showAddForm ? '✕ Annuler' : '+ Ajouter un essai'}
          </button>

          {showAddForm && (
            <div className="form-card">
              <div className="form-group">
                <label className="form-label">Localisation *</label>
                <input
                  type="text"
                  value={currentEssai.localisation}
                  onChange={(e) => setCurrentEssai({...currentEssai, localisation: e.target.value})}
                  placeholder="Ex: ATELIER, SALLE DES MACHINES..."
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Équipement *</label>
                <input
                  type="text"
                  value={currentEssai.equipement}
                  onChange={(e) => setCurrentEssai({...currentEssai, equipement: e.target.value})}
                  placeholder="Ex: TOUR, PERCEUSE, PRESSE..."
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Résultat de l'essai</label>
                <select
                  value={currentEssai.resultat}
                  onChange={(e) => setCurrentEssai({...currentEssai, resultat: e.target.value})}
                  className="form-select"
                >
                  <option value="conforme">✅ Conforme</option>
                  <option value="non_conforme">❌ Non conforme</option>
                  <option value="non_teste">⚠️ Non testé</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Observations</label>
                <textarea
                  value={currentEssai.observations}
                  onChange={(e) => setCurrentEssai({...currentEssai, observations: e.target.value})}
                  placeholder="Remarques éventuelles..."
                  className="form-textarea"
                  rows={3}
                />
              </div>

              <button
                onClick={handleAddEssai}
                disabled={!currentEssai.localisation || !currentEssai.equipement}
                className="save-button"
                style={{ width: '100%' }}
              >
                {editingIndex !== null ? 'Mettre à jour' : 'Ajouter'}
              </button>
            </div>
          )}

          <div className="essais-list">
            {essais.length === 0 ? (
              <div className="empty-state-small">
                <p>Aucun essai enregistré</p>
              </div>
            ) : (
              essais.map((essai, index) => (
                <div key={index} className="essai-card">
                  <div className="essai-header">
                    <div className="essai-info">
                      <strong>{essai.localisation}</strong>
                      <span className="essai-equipment">{essai.equipement}</span>
                    </div>
                    <span className={`resultat-badge resultat-${essai.resultat}`}>
                      {essai.resultat === 'conforme' ? '✅ Conforme' :
                       essai.resultat === 'non_conforme' ? '❌ Non conforme' :
                       '⚠️ Non testé'}
                    </span>
                  </div>
                  
                  {essai.observations && (
                    <p className="essai-observations">{essai.observations}</p>
                  )}
                  
                  <div className="essai-actions">
                    <button
                      onClick={() => handleEditEssai(essai, index)}
                      className="btn-edit-small"
                    >
                      ✏️ Modifier
                    </button>
                    <button
                      onClick={() => handleDeleteEssai(index)}
                      className="btn-delete-small"
                    >
                      🗑️ Supprimer
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="save-button" style={{ width: '100%' }}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}